import { uuid } from '@ctcells/core'
import type { Sample } from '@ctcells/contract'

/** 등록 직후도 in_process다. 미배정 신규 시료에는 재등록을 제안하지 않는다. */
export function canReregisterSample(sample: Sample): boolean {
  return sample.batch_id != null || sample.status !== 'in_process'
}

/** 다시 등록할 때 붙는 꼬리 — 짧은 uuid 8자(eric 2026-10-29). 옛 회차 꼬리 `-rN` 도 같이 뗀다 */
const TRIAL_TAIL = /-(?:r[0-9]+|[0-9a-f]{8})$/

/** 원본 번호 — 꼬리를 겹치지 않게 맨 끝의 `-rN` · `-<8hex>` 를 뗀 것 */
export function trialBaseId(sampleId: string): string {
  return sampleId.replace(TRIAL_TAIL, '')
}

/** 짧은 uuid — 앞 8자(소문자 hex). 🔴 `crypto.randomUUID` 를 직접 부르지 않는다(IP 접속은 보안 컨텍스트가 아니다 · `@ctcells/core` `uuid.ts`) */
export function shortUuid(): string {
  return uuid().replace(/-/g, '').slice(0, 8).toLowerCase()
}

/**
 * 새 시료 번호 = 원본 번호 + `-` + 짧은 uuid 8자. 받아 둔 목록에 이미 있으면 새로 뽑는다.
 * 마지막 판정은 서버(409 `SAMPLE_ALREADY_EXISTS`)다 — 그때는 부르는 쪽이 한 번 더 뽑는다.
 */
export function nextTrialSampleId(
  sourceId: string,
  existing: readonly Sample[],
  next: () => string = shortUuid,
): string {
  const base = trialBaseId(sourceId)
  const taken = new Set(existing.map((sample) => sample.sample_id))
  let id = `${base}-${next()}`
  for (let tries = 0; taken.has(id) && tries < 8; tries += 1) id = `${base}-${next()}`
  return id
}

/** `useRegisterSample` 의 변수 — POST가 받는 등록 정보만 복사한다. 배정·단계·처분·버전은 새 시료로 옮기지 않는다. */
export interface TrialSampleVars {
  sampleId: string
  patientId?: string
  sampleType?: string
  rbcConcentration?: number
  bloodDrawDate?: string
  cancerType?: string
  cancerStage?: string
}

export function trialSampleVars(source: Sample, sampleId: string): TrialSampleVars {
  const text = (value: string | null | undefined) =>
    value == null || value.trim() === '' ? undefined : value
  const patientId = text(source.patient_id)
  const sampleType = text(source.sample_type)
  const bloodDrawDate = text(source.blood_draw_date)
  const cancerType = text(source.cancer_type)
  const cancerStage = text(source.cancer_stage)
  return {
    sampleId,
    ...(patientId === undefined ? {} : { patientId }),
    ...(sampleType === undefined ? {} : { sampleType }),
    ...(bloodDrawDate === undefined ? {} : { bloodDrawDate }),
    ...(cancerType === undefined ? {} : { cancerType }),
    ...(cancerStage === undefined ? {} : { cancerStage }),
    ...(source.rbc_concentration == null ? {} : { rbcConcentration: source.rbc_concentration }),
  }
}
